// Real-time mic amplitude for the dictation waveform. Ported from the design
// system's `use-audio-levels.ts` hook (ui-kit). Independent of
// SpeechRecognition, which exposes no audio data — this opens its own
// getUserMedia stream purely to visualize input level, and nothing captured
// here is stored or sent anywhere.
//
// Levels are a fixed-length rolling history (oldest → newest), so the waveform
// reads as bars scrolling in as the person speaks rather than one bar
// jittering in place. Smoothing is asymmetric — fast attack, slower release —
// the standard VU-meter shape that reads as natural rather than twitchy.
//
// Two things that used to make the mic feel like it took several seconds to
// start, both fixed here and both load-bearing:
// - Every start() opened a brand-new getUserMedia stream with default
//   constraints (echo cancellation / noise suppression / AGC all on) racing
//   SpeechRecognition's own AEC-enabled capture for the same physical device —
//   two processed audio consumers negotiating the same hardware path is a
//   known cause of multi-second stalls in Chrome. We only need raw amplitude,
//   so our stream asks for none of that processing.
// - The stream + AudioContext were torn down on every stop and rebuilt on the
//   next start. They are acquired once per controller lifetime and reused;
//   later starts just resume sampling, and release happens on destroy only.
//
// Acquisition is lazy — the first `start()`, never construction. The drawer
// host mounts on every page load, so an eager acquire would prompt for the
// microphone at login.

export const WAVEFORM_BAR_COUNT = 90;
/** Resting bar height — the flat dotted line. Must match `.gable-mic-bar`. */
export const WAVEFORM_BAR_MIN_PX = 2;
/** Added to the resting height at full scale; min + range must fit the row. */
export const WAVEFORM_BAR_RANGE_PX = 20;
const SAMPLE_INTERVAL_MS = 60;
const ATTACK = 0.6;
const RELEASE = 0.15;

// A fixed gain cannot serve every microphone: a built-in laptop array and a
// headset differ by more than an order of magnitude in RMS for the same voice,
// and the kit's flat `rms * 6` left quieter rigs reading as an almost-flat line
// while someone was speaking normally. So the meter normalizes against what it
// has actually heard instead:
//
// - anything under NOISE_FLOOR is silence and reads as zero, so a quiet room
//   stays a flat dotted line rather than jittering at higher sensitivity;
// - the loudest recent sample becomes the top of the scale, decaying back down
//   so the meter re-adapts after a shout and does not stay permanently squashed;
// - PEAK_FLOOR is the quietest peak allowed to define full scale — without it,
//   near-silence would be stretched to full height and the meter would look
//   like it was hearing speech;
// - the level is then curved with an exponent < 1, because loudness is
//   perceptual: this lifts ordinary speech into the visible part of the range
//   instead of leaving it bunched near the bottom.
//
// PEAK_FLOOR is the one real trade-off, measured across simulated mic gains
// (peak bar height out of 22px, resting line is 2px):
//
//   PEAK_FLOOR   quiet mic   normal speech   steady fan/AC
//   0.06           11.3px          21.8px           7.2px
//   0.03           16.8px          22.0px          10.3px
//   0.015          22.0px          22.0px          15.9px
//
// Lower lifts quiet microphones but lifts steady room noise with them; 0.03
// keeps a quiet laptop mic plainly readable while a fan still sits well under
// speech — and steady noise reads as a flat low band rather than speech shape.
const NOISE_FLOOR = 0.004;
const PEAK_FLOOR = 0.03;
const PEAK_DECAY = 0.992;
const CURVE = 0.6;

const idle = (): number[] => new Array<number>(WAVEFORM_BAR_COUNT).fill(0);

/** 0–1 amplitude samples, oldest → newest, one per bar. */
export type LevelsListener = (levels: number[]) => void;

export class GableAudioLevelsController {
    private _levels: number[] = idle();
    private _smoothed = 0;
    /** Loudest recent sample; the top of the meter's scale. Decays per sample. */
    private _peak = PEAK_FLOOR;

    private _stream: MediaStream | null = null;
    private _audioCtx: AudioContext | null = null;
    private _analyser: AnalyserNode | null = null;
    private _data: Uint8Array | null = null;
    private _acquire: Promise<void> | null = null;

    private _rafId: number | null = null;
    private _lastSample = 0;
    private _active = false;
    /**
     * Bumped by `destroy()`. `getUserMedia` can take multi-second negotiation
     * (see file header); `_acquireStream()` snapshots this before awaiting and
     * checks it after, so a grant that lands post-teardown stops its own
     * tracks and closes its own context instead of re-populating a controller
     * nobody owns.
     */
    private _generation = 0;

    /**
     * @param _listener called with the rolling level history on each sample.
     *   Invoked outside Angular's zone by design — write to the DOM directly
     *   rather than triggering a change-detection pass every 60ms.
     * @param _schedule wraps the rAF loop; the component passes
     *   `fn => ngZone.runOutsideAngular(fn)`. It must run `fn` synchronously:
     *   the double-loop guard in `start()` reads `_rafId` right after, and a
     *   deferring scheduler would leave it null for a sibling to see.
     */
    constructor(
        private readonly _listener: LevelsListener,
        private readonly _schedule: (fn: () => void) => void = (fn) => fn(),
    ) {}

    /** Begin sampling; acquires the mic on the first call and reuses it after. */
    start(): void {
        if (this._active) {
            return;
        }
        this._active = true;

        if (!this._acquire) {
            this._acquire = this._acquireStream();
        }

        // `.then` is always asynchronous, so start → stop → start inside one
        // task queues two of these; without the guards below both would see
        // `_active` and each would schedule its own rAF loop.
        const acquire = this._acquire;
        void acquire.then(() => {
            if (!this._analyser && this._acquire === acquire) {
                // Denied, or no AudioContext. Forget the attempt so the next
                // start() asks again — the blocked-mic notice tells people to
                // allow the microphone and tap the mic a second time, and a
                // cached failure would leave the meter flat for the rest of
                // the session even once they had. Only our own attempt,
                // though: a stale callback must not drop an acquire that a
                // later start() has since begun.
                this._acquire = null;
            }
            if (!this._active || !this._analyser || !this._data) {
                return;
            }
            if (this._rafId !== null) {
                // a sibling callback already has the loop running
                return;
            }
            if (this._audioCtx?.state === 'suspended') {
                void this._audioCtx.resume().catch(() => {});
            }
            this._schedule(() => {
                this._lastSample = 0;
                this._rafId = requestAnimationFrame((time) => this._tick(time));
            });
        });
    }

    /** Stop sampling and flatten the meter. Keeps the stream for the next start. */
    stop(): void {
        this._active = false;
        if (this._rafId !== null) {
            cancelAnimationFrame(this._rafId);
            this._rafId = null;
        }
        this._smoothed = 0;
        this._peak = PEAK_FLOOR;
        this._levels = idle();
        this._listener(this._levels);
    }

    /**
     * Release the microphone and audio graph. Only on the owning component's
     * teardown — doing it per listen/stop is what forced a fresh getUserMedia
     * negotiation on every click.
     */
    destroy(): void {
        this.stop();
        this._generation++;
        this._stream?.getTracks().forEach((track) => track.stop());
        this._stream = null;
        void this._audioCtx?.close().catch(() => {});
        this._audioCtx = null;
        this._analyser = null;
        this._data = null;
        this._acquire = null;
    }

    private async _acquireStream(): Promise<void> {
        const generation = this._generation;
        try {
            const stream = await navigator.mediaDevices?.getUserMedia({
                audio: {
                    echoCancellation: false,
                    noiseSuppression: false,
                    autoGainControl: false,
                },
            });
            if (!stream) {
                return;
            }
            if (generation !== this._generation) {
                // Superseded by a destroy() (or a destroy()+start() pair)
                // while the permission prompt was open — nothing here belongs
                // to the current controller state, so release it unowned.
                stream.getTracks().forEach((track) => track.stop());
                return;
            }
            this._stream = stream;
            const w = window as Window & { webkitAudioContext?: typeof AudioContext };
            const AudioCtor = window.AudioContext ?? w.webkitAudioContext;
            if (!AudioCtor) {
                return;
            }
            const ctx = new AudioCtor();
            if (generation !== this._generation) {
                stream.getTracks().forEach((track) => track.stop());
                void ctx.close().catch(() => {});
                return;
            }
            this._audioCtx = ctx;
            const source = ctx.createMediaStreamSource(stream);
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 256;
            source.connect(analyser);
            this._analyser = analyser;
            this._data = new Uint8Array(analyser.fftSize);
        } catch {
            // the visualizer's own permission request failed — recognition
            // surfaces the real mic-blocked error already, so just stay idle
        }
    }

    private _tick(time: number): void {
        if (!this._active) {
            return;
        }
        this._rafId = requestAnimationFrame((next) => this._tick(next));
        if (time - this._lastSample < SAMPLE_INTERVAL_MS) {
            return;
        }
        this._lastSample = time;

        const analyser = this._analyser;
        const data = this._data;
        if (!analyser || !data) {
            return;
        }

        analyser.getByteTimeDomainData(data);
        let sumSquares = 0;
        for (let i = 0; i < data.length; i++) {
            const centered = (data[i] - 128) / 128;
            sumSquares += centered * centered;
        }
        const rms = Math.sqrt(sumSquares / data.length);

        this._peak = Math.max(rms, this._peak * PEAK_DECAY, PEAK_FLOOR);
        const voiced = Math.max(0, rms - NOISE_FLOOR);
        const span = Math.max(this._peak - NOISE_FLOOR, 1e-6);
        const target = voiced > 0 ? Math.min(1, Math.pow(voiced / span, CURVE)) : 0;

        const rate = target > this._smoothed ? ATTACK : RELEASE;
        this._smoothed += (target - this._smoothed) * rate;

        this._levels = [...this._levels.slice(1), this._smoothed];
        this._listener(this._levels);
    }
}
